import { useCallback, type MouseEvent as ReactMouseEvent } from 'react';

import * as headerCellStyles from '../components/DataTableHeaderCell.sty.js';
import { DATA_TABLE_NARROW_HEADER_THRESHOLDS } from '../constants.js';

interface UseHeaderCellInteractionsParams {
  headerCell: HTMLDivElement | null;
  canSort: boolean;
  canColumnActions: boolean;
  rowDensity: 'default' | 'condensed' | 'comfortable';
  getToggleSortingHandler: () => undefined | ((event: unknown) => void);
}

interface UseHeaderCellInteractionsResult {
  handleCellActionsClose: () => void;
  handleCellMouseEnter: () => void;
  handleCellMouseLeave: () => void;
  handleCellClick: (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * Checks whether an element is interactive.
 */
function isInteractiveElement(element: HTMLElement | SVGElement): boolean {
  const interactiveTags = ['BUTTON', 'A', 'LABEL', 'INPUT', 'SELECT'];

  return !!(
    interactiveTags.includes(element.tagName) ||
    element.getAttribute('tabindex')
  );
}

export function useHeaderCellInteractions({
  headerCell,
  canSort,
  canColumnActions,
  rowDensity,
  getToggleSortingHandler,
}: UseHeaderCellInteractionsParams): UseHeaderCellInteractionsResult {
  const handleCellActionsClose = useCallback((): void => {
    if (headerCell) {
      // remove the hovered and narrow state
      headerCell.classList.remove(
        headerCellStyles.hovered,
        headerCellStyles.narrow,
      );
    }
  }, [headerCell]);

  const handleCellMouseEnter = useCallback((): void => {
    if (!headerCell || (!canColumnActions && !canSort)) {
      return;
    }

    headerCell.classList.add(headerCellStyles.hovered);

    const widthThreshold = DATA_TABLE_NARROW_HEADER_THRESHOLDS[rowDensity];
    if (!widthThreshold) {
      return;
    }

    const headerWidth = headerCell.getBoundingClientRect().width;
    if (headerWidth <= widthThreshold) {
      headerCell.classList.add(headerCellStyles.narrow);
    }
  }, [canSort, canColumnActions, headerCell, rowDensity]);

  const handleCellMouseLeave = useCallback((): void => {
    if (!headerCell) {
      return;
    }
    const cellAction = headerCell.querySelector('[data-trigger="useraction"]');
    const isOpen = cellAction?.getAttribute('data-state') === 'open';
    if (isOpen) {
      // keep the hovered state while the cell actions menu is open
      return;
    }
    // remove the hovered and narrow state
    headerCell.classList.remove(
      headerCellStyles.hovered,
      headerCellStyles.narrow,
    );
  }, [headerCell]);

  const handleCellClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement, MouseEvent>): void => {
      const { target } = event;

      if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) {
        return;
      }

      if ('headerRow' in target.dataset) {
        getToggleSortingHandler?.()?.(event);
        return;
      }

      const columnHeader = target.closest('[role="columnheader"]');
      if (!columnHeader) {
        return;
      }

      let currentElement: typeof target | null = target;
      while (currentElement && currentElement !== columnHeader) {
        if (isInteractiveElement(currentElement)) {
          return;
        }
        currentElement = currentElement.parentElement;
      }

      getToggleSortingHandler?.()?.(event);
    },
    [getToggleSortingHandler],
  );

  return {
    handleCellActionsClose,
    handleCellMouseEnter,
    handleCellMouseLeave,
    handleCellClick,
  };
}
